import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/lib/authContext';
import {
  getConversacionesAsesor,
  getMensajesAsesor,
  createConversacionAsesor,
  sendMensajeAsesor,
  updateConversacionAsesorTitulo,
  deleteConversacionAsesor,
} from '@/lib/supabaseService';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import ChatMarkdown from '@/components/ChatMarkdown';

interface ToolCallInfo {
  name: string;
  args: Record<string, unknown>;
  result: { success: boolean; data?: unknown; error?: string };
}

interface Mensaje {
  id: string;
  rol: 'usuario' | 'asistente';
  contenido: string;
  timestamp: Date;
  fuentes?: string[];
  toolCalls?: ToolCallInfo[];
}

interface Conversacion {
  id: string;
  titulo: string;
  fecha: Date;
  mensajes: Mensaje[];
}

// ─── Tool Result Card Renderer ───
const TOOL_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  listar_clientes: { label: 'Clientes encontrados', icon: 'group', color: 'fresh-sky' },
  listar_facturas: { label: 'Facturas encontradas', icon: 'receipt_long', color: 'steel-azure' },
  crear_factura: { label: 'Factura creada', icon: 'add_circle', color: 'emerald' },
  buscar_documentos: { label: 'Documentos encontrados', icon: 'folder_open', color: 'steel-azure' },
  procesar_ocr: { label: 'OCR completado', icon: 'document_scanner', color: 'fresh-sky' },
  consulta_financiera: { label: 'Resumen financiero', icon: 'analytics', color: 'steel-azure' },
  listar_incidencias: { label: 'Incidencias encontradas', icon: 'report_problem', color: 'amber' },
  crear_incidencia: { label: 'Incidencia creada', icon: 'add_task', color: 'amber' },
};

function ToolCallCard({ toolCall }: { toolCall: ToolCallInfo }) {
  const [expanded, setExpanded] = useState(false);
  const meta = TOOL_LABELS[toolCall.name] || { label: toolCall.name, icon: 'build', color: 'steel-azure' };
  const { result } = toolCall;
  const data = result.data as Record<string, unknown> | undefined;

  const colorMap: Record<string, { bg: string; border: string; text: string; icon: string }> = {
    'fresh-sky': { bg: 'bg-fresh-sky-50', border: 'border-fresh-sky-200', text: 'text-fresh-sky-700', icon: 'text-fresh-sky-500' },
    'steel-azure': { bg: 'bg-steel-azure-50', border: 'border-steel-azure-200', text: 'text-steel-azure-700', icon: 'text-steel-azure-500' },
    'emerald': { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: 'text-emerald-500' },
    'amber': { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: 'text-amber-500' },
  };
  const colors = colorMap[meta.color] || colorMap['steel-azure'];

  if (!result.success) {
    return (
      <div className="mt-2 rounded-xl border border-red-200 bg-red-50 p-3">
        <div className="flex items-center gap-2 text-red-600 text-xs font-medium">
          <span className="material-symbols-outlined text-[14px]">error</span>
          Error: {meta.label}
        </div>
        <p className="text-xs text-red-500 mt-1 font-light">{result.error}</p>
      </div>
    );
  }

  if (toolCall.name === 'crear_factura' && data) {
    return (
      <div className="mt-2 rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-emerald-50/50 p-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="material-symbols-outlined text-[16px] text-emerald-500">check_circle</span>
          <span className="text-xs font-medium text-emerald-700">Factura creada correctamente</span>
        </div>
        <div className="flex items-baseline gap-3 mb-1">
          <span className="text-sm font-medium text-emerald-800">{String(data.numero_factura)}</span>
          <span className="text-lg font-semibold text-emerald-700">
            {Number(data.total).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
          </span>
        </div>
        <div className="flex flex-wrap gap-3 text-[10px] text-emerald-600 font-light">
          <span>Cliente: {String(data.cliente)}</span>
          <span>Base: {Number(data.base_imponible).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</span>
          <span>IVA: {Number(data.impuestos).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</span>
          <span>Líneas: {String(data.lineas)}</span>
        </div>
        <Badge className="mt-2 text-[10px] bg-emerald-100 text-emerald-700 border-emerald-300 rounded-full">
          {String(data.estado)}
        </Badge>
      </div>
    );
  }

  if (toolCall.name === 'crear_incidencia' && data) {
    return (
      <div className="mt-2 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-amber-50/50 p-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="material-symbols-outlined text-[16px] text-amber-500">check_circle</span>
          <span className="text-xs font-medium text-amber-700">{String(data.mensaje || 'Incidencia creada')}</span>
        </div>
        <div className="flex flex-wrap gap-3 text-[10px] text-amber-600 font-light mt-1">
          <Badge className="text-[10px] bg-amber-100 text-amber-700 border-amber-300 rounded-full">{String(data.prioridad)}</Badge>
          <Badge className="text-[10px] bg-amber-100 text-amber-700 border-amber-300 rounded-full">{String(data.estado)}</Badge>
        </div>
      </div>
    );
  }

  if (toolCall.name === 'consulta_financiera' && data) {
    const facturacion = data.facturacion as Record<string, number> | undefined;
    const documentos = data.documentos as Record<string, number> | undefined;
    const incidencias = data.incidencias as Record<string, number> | undefined;
    return (
      <div className="mt-2 rounded-xl border border-steel-azure-200 bg-gradient-to-r from-steel-azure-50 to-fresh-sky-50/50 p-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="material-symbols-outlined text-[16px] text-steel-azure-500">analytics</span>
          <span className="text-xs font-medium text-steel-azure-700">{String(data.cliente)} — Resumen financiero</span>
        </div>
        {facturacion && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
            <div className="bg-white/80 rounded-lg p-2 text-center">
              <div className="text-[10px] text-steel-azure-400 font-light">Total facturado</div>
              <div className="text-sm font-semibold text-steel-azure-700">
                {facturacion.total_facturado?.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
              </div>
            </div>
            <div className="bg-white/80 rounded-lg p-2 text-center">
              <div className="text-[10px] text-steel-azure-400 font-light">Pendiente cobro</div>
              <div className="text-sm font-semibold text-amber-600">
                {facturacion.importe_pendiente?.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
              </div>
            </div>
            <div className="bg-white/80 rounded-lg p-2 text-center">
              <div className="text-[10px] text-steel-azure-400 font-light">Facturas</div>
              <div className="text-sm font-semibold text-steel-azure-700">
                {facturacion.facturas_pagadas}/{facturacion.facturas_total} pagadas
              </div>
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-3 text-[10px] font-light text-steel-azure-500">
          {documentos && <span>Docs: {documentos.pendientes} pendientes / {documentos.contabilizados} contabilizados</span>}
          {incidencias && <span>Incidencias abiertas: {incidencias.abiertas}</span>}
        </div>
      </div>
    );
  }

  if (toolCall.name === 'procesar_ocr' && data) {
    return (
      <div className="mt-2 rounded-xl border border-fresh-sky-200 bg-gradient-to-r from-fresh-sky-50 to-fresh-sky-50/50 p-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="material-symbols-outlined text-[16px] text-fresh-sky-500">document_scanner</span>
          <span className="text-xs font-medium text-fresh-sky-700">Datos extraídos por OCR</span>
          {!!data.confianza && (
            <Badge className="text-[9px] bg-fresh-sky-100 text-fresh-sky-600 border-fresh-sky-300 rounded-full ml-auto">
              {Math.round(Number(data.confianza) * 100)}% confianza
            </Badge>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 text-[11px] font-light">
          {!!data.tipo_documento && <div><span className="text-fresh-sky-400">Tipo:</span> <span className="text-fresh-sky-700">{String(data.tipo_documento)}</span></div>}
          {!!data.numero_documento && <div><span className="text-fresh-sky-400">Nº:</span> <span className="text-fresh-sky-700">{String(data.numero_documento)}</span></div>}
          {!!data.fecha_emision && <div><span className="text-fresh-sky-400">Fecha:</span> <span className="text-fresh-sky-700">{String(data.fecha_emision)}</span></div>}
          {!!data.total && <div><span className="text-fresh-sky-400">Total:</span> <span className="text-fresh-sky-700 font-medium">{Number(data.total).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</span></div>}
        </div>
        {(data.emisor as Record<string, string>)?.nombre && (
          <div className="text-[10px] text-fresh-sky-500 mt-1 font-light">
            Emisor: {(data.emisor as Record<string, string>).nombre} ({(data.emisor as Record<string, string>).cif})
          </div>
        )}
      </div>
    );
  }

  const listKey = toolCall.name === 'listar_clientes' ? 'clientes'
    : toolCall.name === 'listar_facturas' ? 'facturas'
    : toolCall.name === 'buscar_documentos' ? 'documentos'
    : toolCall.name === 'listar_incidencias' ? 'incidencias'
    : null;

  if (listKey && data) {
    const rawItems = Array.isArray(data) ? data : data[listKey];
    const items = (Array.isArray(rawItems) ? rawItems : []) as Record<string, unknown>[];
    const rawTotal = Array.isArray(data) ? data.length : data.total;
    const total = typeof rawTotal === 'number' ? rawTotal : items.length;
    return (
      <div className={cn('mt-2 rounded-xl border p-3', colors.bg, colors.border)}>
        <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={cn('material-symbols-outlined text-[16px]', colors.icon)}>{meta.icon}</span>
            <span className={cn('text-xs font-medium', colors.text)}>{meta.label}</span>
            <Badge className={cn('text-[9px] rounded-full', colors.bg, colors.text, colors.border)}>{total}</Badge>
          </div>
          <span className={cn('material-symbols-outlined text-[16px] transition-transform', colors.icon, expanded && 'rotate-180')}>expand_more</span>
        </button>
        {expanded && items.length > 0 && (
          <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
            {items.slice(0, 10).map((item, idx) => (
              <div key={idx} className="bg-white/80 rounded-lg px-3 py-2 text-[11px] font-light flex flex-wrap gap-x-4 gap-y-0.5">
                {Object.entries(item).filter(([k]) => k !== 'id').slice(0, 5).map(([key, val]) => (
                  <span key={key}>
                    <span className="text-steel-azure-400">{key}:</span>{' '}
                    <span className="text-calm-text">{val != null ? String(val) : '—'}</span>
                  </span>
                ))}
              </div>
            ))}
            {items.length > 10 && (
              <p className={cn('text-[10px] font-light text-center pt-1', colors.text)}>… y {items.length - 10} más</p>
            )}
          </div>
        )}
        {expanded && items.length === 0 && (
          <p className={cn('text-[11px] font-light mt-2', colors.text)}>No se encontraron resultados.</p>
        )}
      </div>
    );
  }

  return (
    <div className={cn('mt-2 rounded-xl border p-3', colors.bg, colors.border)}>
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={cn('material-symbols-outlined text-[16px]', colors.icon)}>{meta.icon}</span>
          <span className={cn('text-xs font-medium', colors.text)}>{meta.label}</span>
        </div>
        <span className={cn('material-symbols-outlined text-[16px] transition-transform', colors.icon, expanded && 'rotate-180')}>expand_more</span>
      </button>
      {expanded && (
        <pre className="mt-2 text-[10px] font-mono text-calm-muted bg-white/80 rounded-lg p-2 overflow-x-auto max-h-48">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}

export default function AsesorAsistente() {
  const { user } = useAuth();

  const [conversaciones, setConversaciones] = useState<Conversacion[]>([]);
  const [conversacionActiva, setConversacionActiva] = useState<Conversacion | null>(null);
  const [mensaje, setMensaje] = useState('');
  const [esperandoRespuesta, setEsperandoRespuesta] = useState(false);
  const [mostrandoSidebar, setMostrandoSidebar] = useState(false);
  const [editandoConversacionId, setEditandoConversacionId] = useState<string | null>(null);
  const [nuevoTitulo, setNuevoTitulo] = useState<string>('');
  const [cargando, setCargando] = useState(true);
  const [conversacionAEliminar, setConversacionAEliminar] = useState<string | null>(null);
  const [archivoAdjunto, setArchivoAdjunto] = useState<File | null>(null);
  const [subiendoArchivo, setSubiendoArchivo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamingMsgRef = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const preguntasSugeridas = [
    '¿Cuáles son mis clientes con facturas pendientes?',
    'Lista las últimas facturas del sistema',
    '¿Hay incidencias abiertas?',
    'Muéstrame el resumen financiero de mis clientes',
    'Busca documentos pendientes de contabilizar',
  ];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversacionActiva?.mensajes]);

  const loadAsesorData = useCallback(async () => {
    if (!user?.id) return;
    try {
      setCargando(true);
      const convs = await getConversacionesAsesor(user.id);
      const conversacionesConMensajes = await Promise.all(
        convs.map(async (conv: any) => {
          const mensajes = await getMensajesAsesor(conv.id);
          return {
            id: conv.id,
            titulo: conv.titulo,
            fecha: conv.fechaCreacion,
            mensajes: mensajes.map((m: any) => ({
              id: m.id,
              rol: m.rol,
              contenido: m.contenido,
              timestamp: m.fecha,
              fuentes: m.metadatos?.fuentes || [],
            })),
          };
        })
      );
      setConversaciones(conversacionesConMensajes);
      if (conversacionesConMensajes.length > 0) {
        setConversacionActiva(conversacionesConMensajes[0]);
      }
    } catch (error) {
      console.error('Error loading advisor data:', error);
    } finally {
      setCargando(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) loadAsesorData();
  }, [user, loadAsesorData]);

  const handleEnviarMensaje = async (e: React.FormEvent | null, mensajeOverride?: string) => {
    if (e) e.preventDefault();
    const textoBase = mensajeOverride || mensaje;
    if (!textoBase.trim() || !user?.id) return;

    let textoFinal = textoBase;
    const archivoParaSubir = mensajeOverride ? null : archivoAdjunto;

    setMensaje('');
    if (!mensajeOverride) setArchivoAdjunto(null);
    setEsperandoRespuesta(true);

    try {
      if (archivoParaSubir) {
        setSubiendoArchivo(true);
        try {
          const formData = new FormData();
          formData.append('file', archivoParaSubir);
          formData.append('asesorId', user.id);
          const uploadRes = await fetch('/api/assistant/upload-file', { method: 'POST', body: formData });
          if (!uploadRes.ok) {
            const errData = await uploadRes.json().catch(() => ({ error: 'Error al subir archivo' }));
            throw new Error(errData.error || `Error ${uploadRes.status}`);
          }
          const uploadData = await uploadRes.json();
          if (uploadData.success) {
            textoFinal = `${textoBase}\n\n[Archivo adjunto: ${uploadData.nombre} (ID: ${uploadData.documentId})]`;
          } else {
            throw new Error(uploadData.error || 'Error al subir archivo');
          }
        } catch (uploadErr) {
          console.error('Error uploading file:', uploadErr);
          const errorMsg: Mensaje = {
            id: Date.now().toString(),
            rol: 'asistente',
            contenido: `Error al subir el archivo: ${uploadErr instanceof Error ? uploadErr.message : 'Error desconocido'}. Por favor, intenta de nuevo.`,
            timestamp: new Date(),
          };
          setConversacionActiva(prev => prev ? { ...prev, mensajes: [...prev.mensajes, errorMsg] } : null);
          setEsperandoRespuesta(false);
          setSubiendoArchivo(false);
          return;
        } finally {
          setSubiendoArchivo(false);
        }
      }

      let convActual = conversacionActiva;
      if (!convActual) {
        const tituloNuevo = textoFinal.substring(0, 50);
        const nuevaId = await createConversacionAsesor(user.id, tituloNuevo);
        const nueva: Conversacion = { id: nuevaId, titulo: tituloNuevo, fecha: new Date(), mensajes: [] };
        convActual = nueva;
        setConversacionActiva(nueva);
        setConversaciones(prev => [nueva, ...prev]);
      }
      const conv = convActual;

      const mensajeId = await sendMensajeAsesor(conv.id, 'usuario', textoFinal);
      const nuevoMensaje: Mensaje = { id: mensajeId, rol: 'usuario', contenido: textoFinal, timestamp: new Date() };
      const conversacionActualizada = { ...conv, mensajes: [...conv.mensajes, nuevoMensaje] };
      setConversacionActiva(conversacionActualizada);
      setConversaciones(prev => prev.map(c => c.id === conv.id ? conversacionActualizada : c));

      const streamBody = JSON.stringify({
        asesorId: user.id,
        conversacionId: conv.id,
        mensaje: textoFinal,
        historial: conv.mensajes.map(m => ({ rol: m.rol, contenido: m.contenido })),
      });

      let streamingContent = '';
      let streamToolCalls: ToolCallInfo[] = [];
      const tempMsgId = `stream-${Date.now()}`;
      streamingMsgRef.current = tempMsgId;
      let streamSuccess = false;

      try {
        const response = await fetch('/api/assistant/asesor-chat-stream', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: streamBody,
        });
        if (!response.ok || !response.body) throw new Error(`Stream error ${response.status}`);

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let sseBuffer = '';
        let readerDone = false;

        while (!readerDone) {
          const { value, done } = await reader.read();
          if (done) { readerDone = true; break; }
          sseBuffer += decoder.decode(value, { stream: true });
          const chunks = sseBuffer.split('\n\n');
          sseBuffer = chunks.pop() ?? '';
          for (const chunk of chunks) {
            const lines = chunk.split('\n');
            let eventType = '';
            let eventData = '';
            for (const line of lines) {
              if (line.startsWith('event: ')) eventType = line.slice(7);
              else if (line.startsWith('data: ')) eventData = line.slice(6);
            }
            if (!eventType || !eventData) continue;
            let parsed: Record<string, unknown>;
            try { parsed = JSON.parse(eventData) as Record<string, unknown>; } catch { continue; }
            if (eventType === 'delta') {
              streamingContent += String(parsed.content ?? '');
              setConversacionActiva(prev => {
                if (!prev) return null;
                const msgs = [...prev.mensajes];
                const existingIdx = msgs.findIndex(m => m.id === tempMsgId);
                const streamMsg: Mensaje = { id: tempMsgId, rol: 'asistente', contenido: streamingContent, timestamp: new Date() };
                if (existingIdx >= 0) msgs[existingIdx] = streamMsg; else msgs.push(streamMsg);
                const updated = { ...prev, mensajes: msgs };
                setConversaciones(pc => pc.map(c => c.id === prev.id ? updated : c));
                return updated;
              });
            } else if (eventType === 'tool_result') {
              const tcInfo: ToolCallInfo = {
                name: String(parsed.name ?? ''),
                args: (parsed.args ?? {}) as Record<string, unknown>,
                result: parsed.result as ToolCallInfo['result'],
              };
              streamToolCalls = [...streamToolCalls, tcInfo];
            } else if (eventType === 'done') {
              const doneTools = parsed.toolCalls as ToolCallInfo[] | undefined;
              if (doneTools && doneTools.length > 0) streamToolCalls = doneTools;
              streamSuccess = true;
            } else if (eventType === 'error') {
              throw new Error(String(parsed.error ?? 'Error de streaming'));
            }
          }
        }
        if (!streamSuccess) streamSuccess = streamingContent.length > 0;
      } catch (streamErr) {
        console.warn('Streaming failed, falling back:', streamErr);
        streamingMsgRef.current = null;
        setConversacionActiva(prev => prev ? { ...prev, mensajes: prev.mensajes.filter(m => m.id !== tempMsgId) } : null);
        const fallbackRes = await fetch('/api/assistant/asesor-chat', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: streamBody });
        if (!fallbackRes.ok) throw new Error(`Error ${fallbackRes.status}`);
        const fbData = await fallbackRes.json();
        if (!fbData.success) throw new Error(fbData.error || 'Error al obtener respuesta');
        streamingContent = fbData.respuesta;
        streamToolCalls = fbData.toolCalls || [];
        streamSuccess = true;
      }

      if (streamSuccess && streamingContent) {
        const fuentes = ['Plan General Contable', 'Normativa fiscal vigente'];
        if (streamToolCalls.length > 0) fuentes.unshift('Datos del sistema Externaliza2');
        const finalToolCalls = streamToolCalls.length > 0 ? streamToolCalls : undefined;
        const respuestaId = await sendMensajeAsesor(conv.id, 'asistente', streamingContent, { fuentes, toolCalls: finalToolCalls });
        const respuestaIA: Mensaje = { id: respuestaId, rol: 'asistente', contenido: streamingContent, timestamp: new Date(), fuentes, toolCalls: finalToolCalls };
        setConversacionActiva(prev => {
          if (!prev) return null;
          const msgs = prev.mensajes.map(m => m.id === tempMsgId ? respuestaIA : m);
          if (!msgs.some(m => m.id === respuestaId)) msgs.push(respuestaIA);
          const updated = { ...prev, mensajes: msgs };
          setConversaciones(pc => pc.map(c => c.id === prev.id ? updated : c));
          return updated;
        });
      }
      streamingMsgRef.current = null;
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMsg: Mensaje = {
        id: Date.now().toString(),
        rol: 'asistente',
        contenido: 'Lo siento, ha ocurrido un error al procesar tu consulta. Por favor, intenta de nuevo.',
        timestamp: new Date(),
      };
      setConversacionActiva(prev => prev ? { ...prev, mensajes: [...prev.mensajes, errorMsg] } : null);
    } finally {
      setEsperandoRespuesta(false);
    }
  };

  const handlePreguntaSugerida = (pregunta: string) => {
    handleEnviarMensaje(null, pregunta);
  };

  const handleNuevaConversacion = async () => {
    if (!user?.id) return;
    try {
      const nuevaId = await createConversacionAsesor(user.id, 'Nueva conversación');
      const nueva: Conversacion = { id: nuevaId, titulo: 'Nueva conversación', fecha: new Date(), mensajes: [] };
      setConversaciones([nueva, ...conversaciones]);
      setConversacionActiva(nueva);
      setEditandoConversacionId(nuevaId);
      setNuevoTitulo('Nueva conversación');
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  const handleIniciarEdicion = (conversacion: Conversacion) => {
    setEditandoConversacionId(conversacion.id);
    setNuevoTitulo(conversacion.titulo);
  };

  const handleGuardarTitulo = async () => {
    if (!editandoConversacionId || !nuevoTitulo.trim()) return;
    try {
      await updateConversacionAsesorTitulo(editandoConversacionId, nuevoTitulo.trim());
      setConversaciones(prev => prev.map(c => c.id === editandoConversacionId ? { ...c, titulo: nuevoTitulo.trim() } : c));
      if (conversacionActiva?.id === editandoConversacionId) {
        setConversacionActiva(prev => prev ? { ...prev, titulo: nuevoTitulo.trim() } : null);
      }
      setEditandoConversacionId(null);
      setNuevoTitulo('');
    } catch (error) {
      console.error('Error updating title:', error);
    }
  };

  const handleCancelarEdicion = () => {
    setEditandoConversacionId(null);
    setNuevoTitulo('');
  };

  const handleEliminarConversacion = (conversacionId: string) => {
    setConversacionAEliminar(conversacionId);
  };

  const confirmarEliminarConversacion = async () => {
    if (!conversacionAEliminar) return;
    try {
      await deleteConversacionAsesor(conversacionAEliminar);
      setConversaciones(prev => prev.filter(c => c.id !== conversacionAEliminar));
      if (conversacionActiva?.id === conversacionAEliminar) {
        const remaining = conversaciones.filter(c => c.id !== conversacionAEliminar);
        setConversacionActiva(remaining.length > 0 ? remaining[0] : null);
      }
      setConversacionAEliminar(null);
    } catch (error) {
      console.error('Error deleting conversation:', error);
      setConversacionAEliminar(null);
    }
  };

  if (cargando) {
    return (
      <Layout rol="asesor">
        <div className="h-[calc(100vh-72px)] flex items-center justify-center">
          <div className="text-center">
            <span className="material-symbols-outlined text-4xl text-calm-muted">progress_activity</span>
            <p className="text-calm-muted font-light mt-2">Cargando asistente...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout rol="asesor">
      {/* Mobile sidebar toggle */}
      <button
        onClick={() => setMostrandoSidebar(!mostrandoSidebar)}
        className="md:hidden fixed top-20 left-4 z-50 btn btn-asesor p-2"
      >
        <span className="material-symbols-outlined text-[18px]">
          {mostrandoSidebar ? 'close' : 'menu'}
        </span>
      </button>

      <div className="h-[calc(100vh-72px)] flex gap-6 p-4 md:p-8">

        {/* ── Sidebar ── */}
        <div className={`
          fixed md:relative inset-0 md:inset-auto z-40 md:z-auto
          bg-white/95 backdrop-blur-xl md:bg-transparent md:backdrop-blur-none
          w-full md:w-80 md:flex-shrink-0
          flex flex-col space-y-4
          p-4 md:p-0
          transition-transform duration-300 ease-in-out
          ${mostrandoSidebar ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}>
          <button
            onClick={() => { handleNuevaConversacion(); setMostrandoSidebar(false); }}
            className="btn btn-asesor w-full"
          >
            <span className="material-symbols-outlined text-sm align-middle">add</span> Nueva Conversación
          </button>

          <div className="card h-[calc(100%-4rem)] overflow-y-auto">
            <h3 className="font-light text-calm-text mb-3">Historial</h3>
            {conversaciones.length === 0 ? (
              <div className="text-center py-10">
                <span className="material-symbols-outlined text-3xl text-calm-muted mb-2 block">forum</span>
                <p className="text-xs text-calm-muted font-light">Sin conversaciones aún.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {conversaciones.map(conv => (
                  <div key={conv.id} className="relative group">
                    {editandoConversacionId === conv.id ? (
                      <div className="p-2 space-y-2">
                        <input
                          type="text"
                          value={nuevoTitulo}
                          onChange={(e) => setNuevoTitulo(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleGuardarTitulo();
                            else if (e.key === 'Escape') handleCancelarEdicion();
                          }}
                          className="input w-full text-sm py-1.5"
                          autoFocus
                        />
                        <div className="flex gap-1.5">
                          <button onClick={handleGuardarTitulo} className="btn btn-asesor btn-sm flex-1">
                            <span className="material-symbols-outlined text-[13px]">check</span> Guardar
                          </button>
                          <button onClick={handleCancelarEdicion} className="btn btn-ghost btn-sm flex-1 border border-calm-border">
                            <span className="material-symbols-outlined text-[13px]">close</span> Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setConversacionActiva(conv); setMostrandoSidebar(false); }}
                        className={`w-full text-left p-3 rounded-huge transition-colors ${
                          conversacionActiva?.id === conv.id
                            ? 'bg-fresh-sky-50 border-2 border-fresh-sky-400'
                            : 'bg-calm-bg hover:bg-calm-bg/80 border-2 border-transparent'
                        }`}
                      >
                        <div className="font-medium text-sm text-calm-text truncate pr-8">
                          {conv.titulo}
                          {conv.mensajes.length > 0 && (
                            <span className="ml-1.5 text-[10px] bg-fresh-sky-100 text-fresh-sky-600 rounded-full px-1.5 py-0.5">
                              {conv.mensajes.length}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-calm-muted mt-1">{conv.fecha.toLocaleDateString('es-ES')}</div>
                        {conv.mensajes.length > 0 && (
                          <p className="text-[11px] text-calm-muted/50 mt-0.5 font-light truncate">
                            {conv.mensajes[conv.mensajes.length - 1].contenido.substring(0, 50)}…
                          </p>
                        )}
                      </button>
                    )}
                    {editandoConversacionId !== conv.id && (
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-0.5">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleIniciarEdicion(conv); }}
                          className="p-1 rounded-lg text-calm-muted hover:text-calm-text hover:bg-calm-border transition-colors"
                          title="Renombrar"
                        >
                          <span className="material-symbols-outlined text-[14px]">edit</span>
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEliminarConversacion(conv.id); }}
                          className="p-1 rounded-lg text-calm-muted hover:text-red-500 hover:bg-red-50 transition-colors"
                          title="Eliminar"
                        >
                          <span className="material-symbols-outlined text-[14px]">delete</span>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Main content ── */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="card mb-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-5xl font-light font-display text-calm-text">Asistente E2</h1>
                <p className="text-calm-muted text-sm mt-1">
                  Inteligencia especializada en contabilidad y fiscalidad — consulta datos reales, crea facturas e incidencias
                </p>
              </div>
              <span className="material-symbols-outlined text-4xl text-calm-accent">psychology</span>
            </div>
          </div>

          {/* Suggested questions */}
          {(!conversacionActiva || conversacionActiva.mensajes.length === 0) && (
            <div className="card mb-4">
              <h3 className="font-light text-calm-text mb-3">Preguntas sugeridas:</h3>
              <div className="grid grid-cols-1 gap-2">
                {preguntasSugeridas.map((pregunta, idx) => (
                  <button
                    key={idx}
                    onClick={() => handlePreguntaSugerida(pregunta)}
                    disabled={esperandoRespuesta}
                    className="text-left p-3 bg-fresh-sky-50/50 hover:bg-fresh-sky-50 rounded-huge text-sm text-calm-text transition-colors disabled:opacity-50"
                  >
                    <span className="material-symbols-outlined text-sm align-middle mr-1 text-fresh-sky-500">chat</span>
                    {pregunta}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 card overflow-y-auto mb-4">
            <div className="space-y-4">
              {conversacionActiva?.mensajes.map(msg => (
                <div key={msg.id} className={`flex ${msg.rol === 'usuario' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-3xl rounded-huge p-4 ${
                    msg.rol === 'usuario'
                      ? 'bg-fresh-sky-600 text-white'
                      : 'bg-calm-bg text-calm-text'
                  }`}>
                    <ChatMarkdown content={msg.contenido} variant={msg.rol === 'usuario' ? 'user' : 'assistant'} />
                    {msg.rol === 'asistente' && msg.toolCalls && msg.toolCalls.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {msg.toolCalls.map((tc, tcIdx) => <ToolCallCard key={tcIdx} toolCall={tc} />)}
                      </div>
                    )}
                    {msg.fuentes && msg.fuentes.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-calm-border/30">
                        <div className="text-xs font-light mb-1 opacity-70">Fuentes:</div>
                        <div className="flex flex-wrap gap-2">
                          {msg.fuentes.map((fuente, idx) => (
                            <span key={idx} className="text-xs bg-white/20 px-2 py-1 rounded-huge">
                              <span className="material-symbols-outlined text-sm align-middle">menu_book</span>{' '}
                              {fuente}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className={`text-xs mt-2 ${msg.rol === 'usuario' ? 'text-white/70' : 'text-calm-muted'}`}>
                      {msg.timestamp.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}

              {esperandoRespuesta && !streamingMsgRef.current && (
                <div className="flex justify-start">
                  <div className="bg-calm-bg rounded-huge p-4">
                    <div className="flex items-center space-x-2">
                      <div className="animate-bounce">●</div>
                      <div className="animate-bounce" style={{ animationDelay: '0.2s' }}>●</div>
                      <div className="animate-bounce" style={{ animationDelay: '0.4s' }}>●</div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input */}
          <form onSubmit={(e) => handleEnviarMensaje(e)} className="card">
            {archivoAdjunto && (
              <div className="flex items-center gap-2 px-3 py-1.5 mb-2 bg-fresh-sky-50 rounded-huge border border-fresh-sky-200/60">
                <span className="material-symbols-outlined text-[14px] text-fresh-sky-500">description</span>
                <span className="text-xs font-light text-fresh-sky-700 flex-1 truncate">{archivoAdjunto.name}</span>
                <span className="text-[10px] text-fresh-sky-400">{(archivoAdjunto.size / 1024).toFixed(0)} KB</span>
                <button type="button" onClick={() => setArchivoAdjunto(null)} className="text-fresh-sky-400 hover:text-fresh-sky-600">
                  <span className="material-symbols-outlined text-[14px]">close</span>
                </button>
              </div>
            )}
            <input
              type="file"
              ref={fileInputRef}
              onChange={(e) => setArchivoAdjunto(e.target.files?.[0] || null)}
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.xlsx,.csv,.xml"
            />
            <div className="flex gap-3 items-center">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-calm-muted hover:text-fresh-sky-600 transition-colors shrink-0"
                disabled={esperandoRespuesta}
                title="Adjuntar archivo"
              >
                <span className="material-symbols-outlined">attach_file</span>
              </button>
              <input
                type="text"
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                placeholder="Pregúntame sobre contabilidad, facturas, clientes..."
                className="input flex-1"
                disabled={esperandoRespuesta}
              />
              <button
                type="submit"
                className="btn btn-asesor"
                disabled={!mensaje.trim() || esperandoRespuesta}
              >
                {subiendoArchivo ? 'Subiendo…' : 'Enviar'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {conversacionAEliminar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card max-w-md w-full">
            <h3 className="text-lg font-light text-calm-text mb-2">Eliminar conversación</h3>
            <p className="text-calm-muted mb-6 font-light">
              ¿Estás seguro de que quieres eliminar esta conversación? Esta acción no se puede deshacer.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConversacionAEliminar(null)}
                className="px-4 py-2 text-calm-text bg-calm-bg rounded-huge hover:bg-calm-border transition-colors font-light text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarEliminarConversacion}
                className="btn btn-danger"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
